export interface SampleViewBox {
  readonly height: number;
  readonly width: number;
}

/** A GeoJSON ring's bounding box, in [minLng, minLat, maxLng, maxLat] order — the projection's input space. */
export type GeometryBoundingBox = readonly [number, number, number, number];

export const boundingBoxOf = (geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | GeoJSON.LineString): GeometryBoundingBox => {
  const points = geometry.type === "LineString"
    ? geometry.coordinates
    : (geometry.type === "Polygon" ? geometry.coordinates : geometry.coordinates.flat()).flat();

  return boundingBoxOfPoints(points as [number, number][]);
};

export const boundingBoxOfPoints = (points: readonly (readonly [number, number])[]): GeometryBoundingBox => {
  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);

  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
};

/**
 * Expands `bounds` to exactly `frameAspectRatio`, correcting longitude for how much shorter its degrees
 * are than latitude's at this latitude (× cos(latitude)). `paddingRatio` pads the latitude span; longitude
 * is always derived from it, never padded independently.
 */
export const correctedFrameBounds = (
  bounds: GeometryBoundingBox,
  paddingRatio: number,
  frameAspectRatio: number
): GeometryBoundingBox => {
  const [minLng, minLat, maxLng, maxLat] = bounds;
  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;
  const latSpan = (maxLat - minLat) * (1 + paddingRatio);
  const longitudeDegreeScale = Math.cos((centerLat * Math.PI) / 180);
  const lngSpan = (frameAspectRatio * latSpan) / longitudeDegreeScale;

  return [centerLng - lngSpan / 2, centerLat - latSpan / 2, centerLng + lngSpan / 2, centerLat + latSpan / 2];
};

/**
 * A rough visual center for label placement — the average of a polygon's own vertices, not a true
 * area-weighted centroid. Not guaranteed to land inside very concave shapes, but close enough at this
 * scale for a zip code's label point.
 */
export const labelPointFor = (geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon): readonly [number, number] => {
  const points = (geometry.type === "Polygon" ? geometry.coordinates : geometry.coordinates.flat()).flat();
  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);

  return [lngs.reduce((sum, lng) => sum + lng, 0) / lngs.length, lats.reduce((sum, lat) => sum + lat, 0) / lats.length];
};

/**
 * Linear bounding-box scale into an SVG viewBox. Pass a shared `bounds` when rendering multiple layers over
 * one frame so they scale together. LineString renders as an open path (no closing "Z").
 */
export const projectGeometryToPath = (
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | GeoJSON.LineString,
  viewBox: SampleViewBox,
  bounds: GeometryBoundingBox
): string => {
  const [minLng, minLat, maxLng, maxLat] = bounds;
  const scaleX = (lng: number): number => ((lng - minLng) / (maxLng - minLng)) * viewBox.width;
  // SVG y grows downward; latitude grows upward — flip it.
  const scaleY = (lat: number): number => (1 - (lat - minLat) / (maxLat - minLat)) * viewBox.height;
  const toPathCommands = (points: GeoJSON.Position[]): string =>
    points.map(([lng, lat], index) => `${index === 0 ? "M" : "L"}${scaleX(lng)},${scaleY(lat)}`).join(" ");

  if (geometry.type === "LineString") return toPathCommands(geometry.coordinates);

  const rings = geometry.type === "Polygon" ? geometry.coordinates : geometry.coordinates.flat();

  return rings.map((ring) => `${toPathCommands(ring)} Z`).join(" ");
};

export const projectPointToPercent = ([lng, lat]: readonly [number, number], bounds: GeometryBoundingBox): { readonly left: number, readonly top: number } => {
  const [minLng, minLat, maxLng, maxLat] = bounds;

  return {
    left: ((lng - minLng) / (maxLng - minLng)) * 100,
    top: (1 - (lat - minLat) / (maxLat - minLat)) * 100,
  };
};

/** CSS background-position/background-size, as plain percentages, for a screenshot behind the SVG. */
export interface SampleBackgroundFit {
  readonly positionX: number;
  readonly positionY: number;
  readonly sizeX: number;
  readonly sizeY: number;
}

const scaledAxisFit = (referencePercent: number, referenceSizePercent: number, spanScale: number): readonly [number, number] => {
  const referenceSize = referenceSizePercent / 100;
  const anchorFraction = (0.5 - (referencePercent / 100) * (1 - referenceSize)) / referenceSize;
  const size = referenceSize / spanScale;
  const percent = (0.5 - anchorFraction * size) / (1 - size);

  return [percent * 100, size * 100];
};

/**
 * Re-derives a background-position/size fit for `bounds` from one already tuned by eye against
 * `referenceBounds` — both share the same center, so this is a zoom-around-center calculation, not a
 * second calibration.
 */
export const scaleBackgroundFit = (
  reference: SampleBackgroundFit,
  referenceBounds: GeometryBoundingBox,
  bounds: GeometryBoundingBox
): SampleBackgroundFit => {
  const lngScale = (bounds[2] - bounds[0]) / (referenceBounds[2] - referenceBounds[0]);
  const latScale = (bounds[3] - bounds[1]) / (referenceBounds[3] - referenceBounds[1]);
  const [positionX, sizeX] = scaledAxisFit(reference.positionX, reference.sizeX, lngScale);
  const [positionY, sizeY] = scaledAxisFit(reference.positionY, reference.sizeY, latScale);

  return { positionX, positionY, sizeX, sizeY };
};
